import Icon, { IconType } from 'components/Icon';
import { Loader } from 'components/Widget/Loader';
import MarkdownText from 'components/Widget/MarkdownText';
import {
	ChatStatus,
	Frame,
	FrameChat,
	SiteFrameEntry,
	SystemChatEntry,
	VisualEditFrameEntry,
} from 'dragonmouse-shared/interfaces';
import { MenuItemAction } from 'menu/MenuItem';
import { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { showContextMenu } from 'redux/mode/actions';
import { useTheme } from 'styled-components';
import { SiteEntry, VisualEditEntry } from '../../../InspectorLeftPanel/Entry';
import FrameChatInput from '../../ChatInput/FrameChatInput';
import { useChatScroll } from '../../hooks/useChatScroll';
import useCopyMessage from '../../hooks/useCopyMessage';
import FeedbackBar from '../FeedbackBar';
import UserMessage from '../UserMessage';
import * as Base from '../styles';
import * as S from './styles';

interface FrameConversationProps {
	frame: Frame;
	frameChat: FrameChat;
}

const FrameConversation = ({ frame, frameChat }: FrameConversationProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const spacerRef = useRef<HTMLDivElement>(null);
	const dispatch = useDispatch();
	const theme = useTheme();
	const { onCopyMessage } = useCopyMessage((msg) => [{ type: 'text', content: msg }] as any);

	const lastEntry =
		frameChat.entries.length > 0 ? frameChat.entries[frameChat.entries.length - 1] : null;
	const lastChatMessage =
		lastEntry && (lastEntry.kind === 'user' || lastEntry.kind === 'system')
			? lastEntry.message
			: null;

	const isChatProcessing =
		frameChat.metadata?.status === ChatStatus.PROCESSING ||
		frameChat.metadata?.status === ChatStatus.STOPPABLE;

	//---------------------------------------------------------------------------------------------------
	// HOOKS
	//---------------------------------------------------------------------------------------------------

	const { isScrolledToBottom, isRestoring, onJumpToBottom } = useChatScroll({
		containerRef,
		spacerRef,
		entries: frameChat.entries,
		activeChatId: frameChat.id,
		lastChatMessage,
		shouldSkipAutoScroll: () => false,
		isInitialized: true,
		scrollAfterUserInput: false,
	});

	//---------------------------------------------------------------------------------------------------
	// ENTRY MERGING
	//---------------------------------------------------------------------------------------------------

	//---------------------------------------------------------------------------------------------------
	// HANDLERS
	//---------------------------------------------------------------------------------------------------

	const onContextMenuInMessage = (e: React.MouseEvent) => {
		const selection = window.getSelection();
		const selectedText = selection?.toString() || null;
		if (selectedText) {
			const menuItems = [MenuItemAction.createCopyText(selectedText)];
			dispatch(
				showContextMenu({
					point: { x: e.clientX + 5, y: e.clientY },
					menuItems,
				})
			);
		}
	};

	//---------------------------------------------------------------------------------------------------
	// RENDERING
	//---------------------------------------------------------------------------------------------------

	const renderEntries = () => {
		return frameChat.entries.map((entry, idx) => {
			if (entry.kind === 'user') {
				return (
					<UserMessage
						key={`user-${idx}`}
						message={entry.message}
						onContextMenu={onContextMenuInMessage}
					/>
				);
			}

			if (entry.kind === 'site') {
				const siteEntry: SiteFrameEntry = {
					kind: 'site',
					id: entry.siteId,
					createdAt: new Date(),
					deletedAt: null,
					siteId: entry.siteId,
					label: entry.label,
					subLabel: entry.subLabel,
				};
				return (
					<S.EntryCard key={`site-${idx}`}>
						<SiteEntry entry={siteEntry} frame={frame} frameId={frame.id} />
					</S.EntryCard>
				);
			}

			if (entry.kind === 'site_ve') {
				const veEntry: VisualEditFrameEntry = {
					kind: 'visualEdit',
					id: entry.id,
					createdAt: new Date(),
					deletedAt: null,
					siteId: entry.siteId,
					visualEdits: entry.visualEdits,
					hash: entry.hash,
				};
				const isActive = frame.metadata.visualEditActive && frame.siteId === entry.siteId;
				return (
					<S.EntryCard key={`ve-${idx}`}>
						<VisualEditEntry
							entry={veEntry}
							frame={frame}
							frameId={frame.id}
							isActive={!!isActive}
						/>
					</S.EntryCard>
				);
			}

			if (entry.kind === 'system') {
				const systemEntry = entry as SystemChatEntry;
				const isLastEntry = idx === frameChat.entries.length - 1;
				return (
					<Base.SystemMessage
						key={`system-${idx}`}
						className="system-message"
						onContextMenu={onContextMenuInMessage}
					>
						<MarkdownText onContextMenu={onContextMenuInMessage}>
							{systemEntry.message}
						</MarkdownText>
						<FeedbackBar
							feedback={systemEntry.feedback}
							isLastEntry={isLastEntry}
							isGenerating={isLastEntry && isChatProcessing}
							onCopy={() => onCopyMessage(idx, systemEntry.message)}
						/>
					</Base.SystemMessage>
				);
			}

			return null;
		});
	};

	const renderStatus = () => {
		if (!frameChat.metadata?.statusMessage) return null;
		if (!frameChat.metadata?.status) return null;

		const { status, statusMessage } = frameChat.metadata;
		if (status !== ChatStatus.PROCESSING && status !== ChatStatus.STOPPABLE) {
			return null;
		}

		return (
			<Base.StatusMessage>
				<Base.StatusText>{statusMessage}</Base.StatusText>
				<Loader size={16} />
			</Base.StatusMessage>
		);
	};

	return (
		<Base.Base>
			<Base.ScrollingAreaContainer $fill={frameChat.entries.length > 0}>
				<Base.ScrollingArea
					ref={containerRef}
					data-chat-scroll-container
					$fill={frameChat.entries.length > 0}
				>
					{renderEntries()}
					{renderStatus()}
					<div ref={spacerRef} />
				</Base.ScrollingArea>
				{isRestoring && (
					<Base.RestoreOverlay>
						<Loader size={24} />
					</Base.RestoreOverlay>
				)}
			</Base.ScrollingAreaContainer>
			<Base.InputArea>
				{!isScrolledToBottom && !isRestoring && (
					<Base.JumpButton onClick={onJumpToBottom}>
						<Base.JumpButtonIcon>
							<Icon
								type={IconType.LINE_ARROW_DOWN}
								width={18}
								height={18}
								color={theme.gray[700]}
							/>
						</Base.JumpButtonIcon>
					</Base.JumpButton>
				)}
				<FrameChatInput frame={frame} frameChat={frameChat} />
			</Base.InputArea>
		</Base.Base>
	);
};

export default FrameConversation;
